import React from "react";
import { motion } from "framer-motion";
import ActionsDropdown from "./ActionsDropdown";
import {
  Calendar,
  Clock,
  ChevronDown,
  CalendarCheck,
  CalendarArrowDown,
  CalendarArrowUp,
  Pin,
  Stethoscope,
  Users,
  IdCard,
  HeartPulse,
  DollarSign,
  BanknoteArrowDown,
  Check,
  MoreVertical,
} from "lucide-react";
import { FaUserDoctor } from "react-icons/fa6";
import { arClass } from "../../../utils/useArClass";
import formatDate from "../../../utils/formatDate";
import formatTime from "../../../utils/formatTime";
import typeTranslator from "../../../utils/useTypeTranslator";
import StatusAlert from "react-status-alert";
import Txt from "../../../components/Text";

const Text = ({ children, className, page = "WAITING_ROOM" }) => (
  <Txt page={page} className={className}>
    {children}
  </Txt>
);

const WaitingRoomPatientItem = ({
  entry,
  minutes,
  urgency,
  sectionState,
  setExpandedSections,
  assignedDoctorName,
  assignedNurseName,
  creatorName,
  paymentInfo,
  patientName,
  cardBg,
  cardText,
  darkMode,
  defaultLanguage,
  statusInfo,
  statusBadge,
  urgencyBadge,
  selected,
  handleSelectItem,
  canDeleteEntries,
  toggleActions,
  actionsOpenId,
  anchorEl,
  onCloseActions,
  actionLoading,
  showActionDescription,
  setShowActionDescription,
  theme,
  roles,
  isAdmin,
  handleStatusChange,
  handleDeleteEntry,
  navigate,
}) => {
  const statusKeyMap = {
    waiting: "statusWaiting",
    with_nurse: "statusWithNurse",
    with_doctor: "statusWithDoctor",
    done: "statusDone",
    cancelled: "statusCancelled",
    absent: "statusAbsent",
    payment: "statusPayment",
  };

  return (
    <div
      key={entry.id}
      className={`group ${cardBg} ${cardText} rounded-2xl p-5 border shadow-sm transition-all duration-200 hover:shadow-sm hover:border-orange-400 relative overflow-hidden flex flex-col gap-4 max-sm:p-4 ${
        statusInfo.border
      }`}
    >
      <div className={arClass("absolute top-2 right-2 z-30")}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => handleSelectItem(entry.id)}
          disabled={canDeleteEntries ? false : true}
          className="w-4 h-4 cursor-pointer rounded-md"
        />
      </div>

      <div className={arClass("flex justify-between items-start gap-3 mt-4")}>
        <div>
          <h3 className={arClass("font-bold text-lg sm:text-xl leading-tight max-sm:text-sm")}>
            {patientName}
          </h3>
          <div className={arClass("flex items-center gap-2 text-xs font-medium mt-1.5 text-slate-400")}>
            {entry.position !== null && (
              <span className={arClass("bg-slate-100/40 text-slate-500 px-2 py-0.5 rounded-full max-sm:text-xs")}>
                <Text>position</Text> #{entry.position}
              </span>
            )}
            <span className={arClass("flex items-center gap-1 max-sm:text-xs")}>
              <Clock size={12} /> {minutes} <Text>wait</Text>
            </span>
          </div>
        </div>
        <div className={arClass("flex flex-col  gap-2")}>
          <span
            className={arClass(
              `px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide max-sm:text-xsm flex justify-center items-center ${
                statusBadge || "bg-slate-100 text-slate-500 border border-slate-200"
              }`,
            )}
          >
            <Text>{statusKeyMap[entry.status]}</Text>
          </span>
          <span
            className={arClass(
              `px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide max-sm:!text-xsm flex justify-center items-center ${urgencyBadge}`,
            )}
          >
            <Text>{urgency}</Text>
          </span>
        </div>
      </div>

      <div className={arClass(`${darkMode ? "bg-slate-900/40" : "bg-slate-50"} rounded-xl p-3`)}>
        {entry.appointment && (
          <div className={arClass("mt-3 pt-3")}>
            <button
              type="button"
              onClick={() =>
                setExpandedSections((prev) => ({
                  ...prev,
                  [entry.id]: {
                    ...sectionState,
                    appointment: !sectionState.appointment,
                  },
                }))
              }
              className={arClass(
                `w-full flex items-center justify-between rounded-lg px-2.5 py-2 text-xs font-semibold transition ${
                  darkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"
                }`,
              )}
            >
              <span className={arClass("flex items-center gap-2")}>
                <Calendar className={arClass("w-3.5 h-3.5")} />
                <Text page="APPOINTMENTS">Appointment Details</Text>
              </span>
              <motion.span animate={{ rotate: sectionState.appointment ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className={arClass("w-4 h-4")} />
              </motion.span>
            </button>

            <motion.div
              initial={false}
              animate={{
                height: sectionState.appointment ? "auto" : 0,
                opacity: sectionState.appointment ? 1 : 0,
              }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className={arClass("pt-2 px-2.5 py-2 flex flex-wrap text-xs text-indigo-600 gap-2 max-sm:text-xsm")}>
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Calendar className={arClass("text-xs w-3.5 h-3.5")} />
                  <Text>appointment</Text>
                </span>
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  #{entry.appointment.id}
                </span>
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <StatusAlert className={arClass("text-xs w-3.5 h-3.5")} />
                  <Text page="APPOINTMENTS">{entry.appointment.status}</Text>
                </span>
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Stethoscope className={arClass("text-xs w-3.5 h-3.5")} />
                  {typeTranslator(entry.appointment.appointmentReason).translatedType}
                </span>
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <CalendarCheck className={arClass("text-xs w-3.5 h-3.5")} />
                  {formatDate(entry.appointment.appointmentDate)}
                </span>
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Clock className={arClass("text-xs w-3.5 h-3.5")} />
                  {formatTime(entry.appointment.appointmentTime)}
                </span>
              </div>
            </motion.div>
          </div>
        )}

        <div className={arClass(`mt-3 pt-3 ${entry.appointment ? "border-t border-slate-200/60" : ""}`)}>
          <button
            type="button"
            onClick={() =>
              setExpandedSections((prev) => ({
                ...prev,
                [entry.id]: {
                  ...sectionState,
                  patientDetails: !sectionState.patientDetails,
                },
              }))
            }
            className={arClass(
              `w-full flex items-center justify-between rounded-lg px-2.5 py-2 text-xs font-semibold transition ${
                darkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"
              }`,
            )}
          >
            <span className={arClass("flex items-center gap-2")}>
              <HeartPulse className={arClass("w-3.5 h-3.5")} />
              <Text page="VISITS">Patient Details</Text>
            </span>
            <motion.span animate={{ rotate: sectionState.patientDetails ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown className={arClass("w-4 h-4")} />
            </motion.span>
          </button>

          <motion.div
            initial={false}
            animate={{
              height: sectionState.patientDetails ? "auto" : 0,
              opacity: sectionState.patientDetails ? 1 : 0,
            }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className={arClass("pt-2 px-2.5 py-2 flex flex-wrap text-xs text-indigo-600 gap-2 max-sm:text-xsm")}>
              <div className={arClass("flex items-center gap-1")}>
                <IdCard className={arClass("w-3.5 h-3.5")} />
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Text page="PATIENTS">ID</Text>: {entry.patientId || "-"}
                </span>
              </div>
              <div className={arClass("flex items-center gap-1")}>
                <MoreVertical className={arClass("w-3.5 h-3.5")} />
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Text page="PATIENTS">Phone</Text>: {entry.patient?.phone || "-"}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        <div className={arClass(` ${entry.appointment ? "border-t border-slate-200/60 mt-3 pt-3" : ""}`)}>
          <button
            type="button"
            onClick={() =>
              setExpandedSections((prev) => ({
                ...prev,
                [entry.id]: {
                  ...sectionState,
                  visitDetails: !sectionState.visitDetails,
                },
              }))
            }
            className={arClass(
              `w-full flex items-center justify-between rounded-lg px-2.5 py-2 text-xs font-semibold transition ${
                darkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"
              }`,
            )}
          >
            <span className={arClass("flex items-center gap-2")}>
              <IdCard className={arClass("w-3.5 h-3.5")} />
              <Text page="VISITS">Visit Details</Text>
            </span>
            <motion.span animate={{ rotate: sectionState.visitDetails ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <ChevronDown className={arClass("w-4 h-4")} />
            </motion.span>
          </button>

          <motion.div
            initial={false}
            animate={{
              height: sectionState.visitDetails ? "auto" : 0,
              opacity: sectionState.visitDetails ? 1 : 0,
            }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className={arClass("pt-2 px-2.5 py-2 flex flex-wrap text-xs text-indigo-600 gap-2 max-sm:text-xsm")}>
              <div className={arClass("flex items-center gap-1")}>
                <Pin className={arClass("w-3.5 h-3.5")} />
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Text page="VISITS">Visit ID</Text>: {entry.visitId || "-"}
                </span>
              </div>
              <div className={arClass("flex items-center gap-1")}>
                <HeartPulse className={arClass("w-3.5 h-3.5")} />
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Text page="VISITS">Visit status</Text>: <Text page="VISITS">{entry.visit?.status || "-"}</Text>
                </span>
              </div>
              <div className={arClass("flex items-center gap-1")}>
                <FaUserDoctor className={arClass("w-3.5 h-3.5")} />
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Text page="VISITS">Doctor</Text>: {assignedDoctorName}
                </span>
              </div>
              <div className={arClass("flex items-center gap-1")}>
                <Stethoscope className={arClass("w-3.5 h-3.5")} />
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Text page="VISITS">Nurse</Text>: {assignedNurseName}
                </span>
              </div>
              <div className={arClass("flex items-center gap-1 sm:col-span-2")}>
                <Users className={arClass("w-3.5 h-3.5")} />
                <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
                  <Text page="VISITS">Created by</Text>: {creatorName}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {paymentInfo && (
          <div className={arClass("mt-3 pt-3 border-t border-slate-200/60")}>
            <button
              type="button"
              onClick={() =>
                setExpandedSections((prev) => ({
                  ...prev,
                  [entry.id]: {
                    ...sectionState,
                    payment: !sectionState.payment,
                  },
                }))
              }
              className={arClass(
                `w-full flex items-center justify-between rounded-lg px-2.5 py-2 text-xs font-semibold transition ${
                  darkMode ? "text-slate-200 hover:bg-slate-800" : "text-slate-700 hover:bg-slate-100"
                }`,
              )}
            >
              <span className={arClass("flex items-center gap-2")}>
                <DollarSign className={arClass("w-3.5 h-3.5")} />
                <Text page="PAYMENTS">Payment Details</Text>
              </span>
              <motion.span animate={{ rotate: sectionState.payment ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className={arClass("w-4 h-4")} />
              </motion.span>
            </button>

            <motion.div initial={false} animate={{ height: sectionState.payment ? "auto" : 0, opacity: sectionState.payment ? 1 : 0 }} transition={{ duration: 0.25, ease: "easeInOut" }} className="overflow-hidden">
              <div className={arClass("pt-2 px-2.5 py-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs max-sm:text-xsm")}>
                <div className={arClass("rounded-lg border border-slate-200/70 px-2.5 py-2 text-green-700")}>
                  <div className={arClass("flex items-center gap-1 font-semibold")}>
                    <DollarSign className={arClass("w-3.5 h-3.5")} />
                    <span>
                      <Text page="PAYMENTS">Amount</Text>
                    </span>
                  </div>
                  <p className={arClass("mt-1")}>{paymentInfo?.amount ? `${paymentInfo.amount}` : "-"}</p>
                </div>
                <div className={arClass("rounded-lg border border-slate-200/70 px-2.5 py-2 text-green-700")}>
                  <div className={arClass("flex items-center gap-1 font-semibold")}>
                    <BanknoteArrowDown className={arClass("w-3.5 h-3.5")} />
                    <span>
                      <Text page="PAYMENTS">Method</Text>
                    </span>
                  </div>
                  <p className={arClass("mt-1")}>
                    <Text page="PAYMENTS">{paymentInfo?.method || "-"}</Text>
                  </p>
                </div>
                <div className={arClass("rounded-lg border border-slate-200/70 px-2.5 py-2 text-green-700")}>
                  <div className={arClass("flex items-center gap-1 font-semibold")}>
                    <Check className={arClass("w-3.5 h-3.5")} />
                    <span>
                      <Text page="PAYMENTS">Status</Text>
                    </span>
                  </div>
                  <p className={arClass("mt-1")}>
                    <Text page="PAYMENTS">{paymentInfo?.status || "-"}</Text>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        <div className={arClass("mt-3 flex flex-col pt-3 border-t border-slate-200/60 text-xs text-indigo-600  gap-2 max-sm:text-xsm")}>
          <div className={arClass("mt-3 flex items-center  text-xs gap-1 max-sm:text-xsm")}>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
              <Calendar className={arClass("text-xs w-3.5 h-3.5")} />
              <Text>Called at</Text>:
            </span>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>{entry.calledAt ? formatDate(entry.calledAt, true) : "-"}</span>
          </div>
          <div className={arClass("mt-3 flex items-center  text-xs gap-1 max-sm:text-xsm")}>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
              <CalendarArrowDown className={arClass("text-xs w-3.5 h-3.5")} />
              <Text>enteredAt</Text>:
            </span>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>{formatDate(entry.enteredAt, true)}</span>
          </div>
          <div className={arClass("mt-3 flex items-center  text-xs gap-1 max-sm:text-xsm")}>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
              <CalendarArrowUp className={arClass("text-xs w-3.5 h-3.5")} />
              <Text>leftAt</Text>:
            </span>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
              {entry.leftAt ? formatDate(entry.leftAt, true) : <>-</>}
            </span>
          </div>
          <div className={arClass("mt-3 flex items-center  text-xs gap-1 max-sm:text-xsm")}>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
              <CalendarCheck className={arClass("text-xs w-3.5 h-3.5")} />
              Finished at:
            </span>
            <span className={arClass("text-xs max-sm:text-xsm flex gap-0.5 items-center")}>
              {entry.finishedAt ? formatDate(entry.finishedAt, true) : "-"}
            </span>
          </div>
        </div>
        {entry.status === "payment" && (
          <motion.button onClick={() => navigate(`/billing/${entry.visitId}`)} className={arClass(`flex items-center gap-2 mt-4 w-max px-3 py-2 rounded-lg  text-sm font-medium ${darkMode ? "bg-green-600 text-white hover:bg-green-700" : "bg-green-100 text-green-800 hover:bg-green-200"}`)}>
            <BanknoteArrowDown className="w-4 h-4 inline-block mr-1" />
            <Text>collectBilling</Text>
          </motion.button>
        )}
      </div>

      <div className={"flex items-center gap-2 mt-auto pt-2"}>
        <div className={arClass(`flex-1 text-center rounded-lg py-2.5 text-sm font-medium  italic max-sm:text-xs max-sm:py-2 ${statusInfo.color}`)}>
          <Text>{statusKeyMap[entry.status]}</Text>
        </div>

        <ActionsDropdown
          entry={entry}
          actionsOpenId={actionsOpenId}
          anchorEl={anchorEl}
          onCloseActions={onCloseActions}
          defaultLanguage={defaultLanguage}
          darkMode={darkMode}
          theme={theme}
          actionLoading={actionLoading}
          showActionDescription={showActionDescription}
          setShowActionDescription={setShowActionDescription}
          roles={roles}
          isAdmin={isAdmin}
          handleStatusChange={handleStatusChange}
          handleDeleteEntry={handleDeleteEntry}
          toggleActions={toggleActions}
        />
      </div>
    </div>
  );
};

export default WaitingRoomPatientItem;
